import type {
  LastPlayed,
  ProgressPeriod,
  RecentGameCardModel,
  ThisWeekSummary,
} from '../types'
import { formatPeriodLabel } from './periodLabel'
import { buildThisWeekRadar } from './thisWeekRadar'

export interface BuildThisWeekSummaryArgs {
  thisWeek: ProgressPeriod
  /** The loaded range's periods, which scale the radar. */
  series: ProgressPeriod[]
  streakWeeks: number
  isAllSports: boolean
  /** The most recent game, for the last-played line. */
  lastGame: RecentGameCardModel | undefined
  /** Abbr → full stat name, for the week's stat labels. */
  statNames: Record<string, string>
}

const NO_STREAK_LABEL = 'No active streak'

const formatDateRange = (week: ProgressPeriod): string =>
  `${formatPeriodLabel(week.period_start, 'week')} – ${formatPeriodLabel(
    week.period_end,
    'week'
  )}`

const formatLastPlayed = (game: RecentGameCardModel): LastPlayed => ({
  text: `Last played ${game.date} · ${game.matchup}`,
  result: game.result,
})

const formatStreak = (streakWeeks: number): string =>
  streakWeeks > 0 ? `${streakWeeks}-week streak` : NO_STREAK_LABEL

/**
 * The This Week strip: the week's games, its stats (the radar axes), and when
 * the player last played. All Sports shows only the games count.
 */
export const buildThisWeekSummary = ({
  thisWeek,
  series,
  streakWeeks,
  isAllSports,
  lastGame,
  statNames,
}: BuildThisWeekSummaryArgs): ThisWeekSummary => {
  const hasGames = thisWeek.games_played > 0
  const showStats = hasGames && !isAllSports
  const showLastPlayed = !isAllSports && lastGame !== undefined
  return {
    dateRange: formatDateRange(thisWeek),
    gamesPlayed: thisWeek.games_played,
    hasGames,
    radar: showStats
      ? buildThisWeekRadar({ thisWeek, series, statNames })
      : null,
    lastPlayed: showLastPlayed ? formatLastPlayed(lastGame) : null,
    streakLabel: formatStreak(streakWeeks),
    hasStreak: streakWeeks > 0,
  }
}
